import { createCommandBarDataAttribute } from 'utils/commandBarHelpers';

/**
 * Available CommandBar sections for the application
 */
// eslint-disable-next-line no-shadow
export enum CommandBarSections {
  REPORTS = 'reports',
  CONFIGURATION = 'configuration',
  MENUS = 'menus',
  PERSONNEL = 'personnel',
  INVENTORIES = 'inventories',
  INVOICING = 'invoicing',
  SUBSCRIPTIONS = 'subscriptions',
}

/**
 * Hook for generating CommandBar attributes for specific sections (reports, configuration, etc.)
 *
 * @description Provides a convenient way to generate data-commandbar-id attributes
 * for all titles and headers within different sections. This hook is focused solely
 * on CommandBar functionality and does not include styling - styles should be handled
 * by the consuming components.
 *
 * @param {CommandBarSections} [section=CommandBarSections.REPORTS] - The section name for the CommandBar attribute
 * @returns {object} Object with helper functions for creating CommandBar attributes
 * @returns {function} returns.createTitleAttribute - Function to create data-commandbar-id attributes for titles
 *
 * @example
 * const commandBarHook = useCommandBarAttributes(CommandBarSections.CONFIGURATION);
 * const titleAttributes = commandBarHook.createTitleAttribute('Settings Title');
 * // Returns: { 'data-commandbar-id': 'configuration-settings-title' }
 *
 * const actionAttributes = commandBarHook.createTitleAttribute('Settings Title', 'editar');
 * // Returns: { 'data-commandbar-id': 'configuration-settings-title-editar' }
 */
const useCommandBarAttributes = (section: CommandBarSections = CommandBarSections.REPORTS) => {
  /**
   * Creates CommandBar data attributes for titles
   *
   * @param {string} title - The title text to generate CommandBar ID for
   * @param {string} [action] - Optional action to append (e.g., 'agregar', 'editar')
   * @returns {object} Object containing data-commandbar-id attribute
   *
   * @example
   * createTitleAttribute('Sales Report');
   * // Returns: { 'data-commandbar-id': 'reports-sales-report' }
   *
   * createTitleAttribute('Sales Report', 'editar');
   * // Returns: { 'data-commandbar-id': 'reports-sales-report-editar' }
   */
  const createTitleAttribute = (title: string, action?: string): object =>
    createCommandBarDataAttribute(section, title, action);

  return {
    createTitleAttribute,
  };
};

/**
 * Hook for generating CommandBar attributes specifically for reports section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in reports
 *
 * @example
 * const commandBarHook = useCommandBarReports();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('reports:salesReport.title')}
 * />
 */
export const useCommandBarReports = () => useCommandBarAttributes(CommandBarSections.REPORTS);

/**
 * Hook for generating CommandBar attributes specifically for configuration section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in configuration
 *
 * @example
 * const commandBarHook = useCommandBarConfiguration();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('settings:sections.parrotPay.title')}
 *   description={t('settings:sections.parrotPay.description')}
 * />
 */
export const useCommandBarConfiguration = () => useCommandBarAttributes(CommandBarSections.CONFIGURATION);

/**
 * Hook for generating CommandBar attributes specifically for menus section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in menus
 *
 * @example
 * const commandBarHook = useCommandBarMenus();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('menus:management.title')}
 *   action="agregar"
 * />
 */
export const useCommandBarMenus = () => useCommandBarAttributes(CommandBarSections.MENUS);

/**
 * Hook for generating CommandBar attributes specifically for personnel section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in personnel
 *
 * @example
 * const commandBarHook = useCommandBarPersonnel();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('employees:management.title')}
 *   action="editar"
 * />
 */
export const useCommandBarPersonnel = () => useCommandBarAttributes(CommandBarSections.PERSONNEL);

/**
 * Hook for generating CommandBar attributes specifically for inventories section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in inventories
 *
 * @example
 * const commandBarHook = useCommandBarInventories();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('inventories:stock.title')}
 *   action="agregar"
 * />
 */
export const useCommandBarInventories = () => useCommandBarAttributes(CommandBarSections.INVENTORIES);

/**
 * Hook for generating CommandBar attributes specifically for billing section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in billing
 *
 * @example
 * const commandBarHook = useCommandBarBilling();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('billing:invoice.settings.title')}
 *   action="editar"
 * />
 */
export const useCommandBarInvoicing = () => useCommandBarAttributes(CommandBarSections.INVOICING);

/**
 * Hook for generating CommandBar attributes specifically for subscriptions section
 *
 * @returns {object} Object with helper functions for creating CommandBar attributes in subscriptions
 *
 * @example
 * const commandBarHook = useCommandBarSubscriptions();
 * <TitleSection
 *   commandBarHook={commandBarHook}
 *   title={t('subscriptions:plans.title')}
 *   action="agregar"
 * />
 */
export const useCommandBarSubscriptions = () => useCommandBarAttributes(CommandBarSections.SUBSCRIPTIONS);

export default useCommandBarAttributes;
